/**
 * superadminPricingService -- thin axios wrappers for the SuperAdmin
 * tier/version + grandfathering admin API (PR-6 of the distributor-payments
 * redesign). Card: "Subscription and Stripe integration"
 * (https://trello.com/c/pTVxuNGQ).
 *
 * Covers every route in `api/src/endpoints/superadmin_pricing.py`
 * (all SA-gated on `superadmin.settings`):
 *
 *   GET  /api/superadmin/pricing/tiers
 *   POST /api/superadmin/pricing/tiers
 *   GET  /api/superadmin/pricing/plan-versions
 *   POST /api/superadmin/pricing/plan-versions
 *   POST /api/superadmin/pricing/plan-versions/:id/repoint
 *   GET  /api/superadmin/pricing/plan-versions/:id/distributors
 *   GET   /api/superadmin/pricing/tier-suppliers
 *   PUT   /api/superadmin/pricing/tier-suppliers
 *   GET   /api/superadmin/pricing/tier-white-label
 *   PATCH /api/superadmin/pricing/tier-white-label
 *   GET  /api/superadmin/distributors/:id/supplier-overrides
 *   PUT  /api/superadmin/distributors/:id/supplier-overrides
 *
 * Conventions (match DistributorEdit.vue / GlobalDefaultMargin.vue):
 *   - Use the shared `@/axios` instance with full `/api/...` paths (the
 *     axios interceptor de-dupes a doubled `/api` prefix).
 *   - Methods return the axios promise; callers `await` and read `.data`.
 *   - Auth header is automatic via the axios interceptor.
 */
import axios from "@/axios";

const BASE = "/api/superadmin/pricing";

/**
 * List the platform plan catalog for the SA Plans & Pricing page.
 *
 * Backs PlansPricing.vue (card https://trello.com/c/goUflLxO). Each row:
 *   { id, name, monthly_price_cents, per_store_price_cents, store_limit,
 *     included_orders,
 *     overage_cents }
 * Hidden plans (e.g. the per-seat add-on) are excluded server-side.
 */
export function listPlans() {
    return axios.get(`${BASE}/plans`);
}

/**
 * Set the plan display order. Wraps PUT /api/superadmin/pricing/plans/order.
 * `planIds` must be EVERY non-archived plan id exactly once, in display order.
 */
export function reorderPlans(planIds) {
    return axios.put(`${BASE}/plans/order`, { plan_ids: planIds });
}

export function listTiers() {
    return axios.get(`${BASE}/tiers`);
}

export function createTier(payload) {
    return axios.post(`${BASE}/tiers`, payload);
}

export function listPlanVersions(tierKey = null) {
    const url = tierKey
        ? `${BASE}/plan-versions?tier_key=${encodeURIComponent(tierKey)}`
        : `${BASE}/plan-versions`;
    return axios.get(url);
}

export function createPlanVersion(payload) {
    return axios.post(`${BASE}/plan-versions`, payload);
}

/**
 * Repoint one or many distributors onto a plan version. Accepts an array of
 * ids (normalized to the bulk shape the backend expects).
 */
export function repointDistributors(planVersionId, distributorIds) {
    return axios.post(`${BASE}/plan-versions/${planVersionId}/repoint`, {
        distributor_ids: distributorIds,
    });
}

export function distributorsOnVersion(planVersionId) {
    return axios.get(`${BASE}/plan-versions/${planVersionId}/distributors`);
}

/**
 * Patch a plan's mutable fields. Wraps PATCH /api/superadmin/pricing/plans/:id.
 *
 * Whitelisted fields (all optional):
 *   title               -- display name
 *   store_limit         -- int|null (INCLUDED/free stores, NOT a cap;
 *                          null = unlimited)
 *   included_orders     -- int|null
 *   overage_cents       -- int|null
 *   monthly_price_cents -- int|null (API converts to price float)
 *   per_store_price_cents -- int|null (the per-live-store monthly charge;
 *                          null REMOVES it, 0 is a real $0-per-store price).
 *                          Rejected with 409 while the plan has active
 *                          subscribers -- it is a price field, so it must
 *                          move via the version/repoint machinery.
 *
 * @param {number|string} planId   Plan id.
 * @param {object}        payload  Subset of whitelisted fields.
 * @returns {Promise} Axios response; .data is the updated plan row.
 */
export function patchPlan(planId, payload) {
    return axios.patch(`${BASE}/plans/${planId}`, payload);
}

/**
 * Set a plan's "Per active store" (store_class "full") or "Per dormant
 * store" ("dormant") price. Wraps
 * POST /api/superadmin/pricing/plans/:id/store-class-price.
 *
 * PUBLISHES A NEW PLAN VERSION (never an in-place edit), so it is allowed on
 * a plan with active subscribers: they keep their pinned price until
 * repointed. A no-op (same amount) returns version: null.
 *
 * @param {number|string} planId  Plan id.
 * @param {object} payload  { store_class: "full"|"dormant", amount_cents: int >= 0 }.
 * @returns {Promise} Axios response; .data is { plan, version } where plan is
 *   the list_plans row (per_active_store_cents / per_dormant_store_cents) and
 *   version is { id, version_label, superseded_version_id, charge_id } | null.
 */
export function setStoreClassPrice(planId, payload) {
    return axios.post(`${BASE}/plans/${planId}/store-class-price`, payload);
}

/**
 * Set how many ACTIVE stores a plan includes free. Wraps
 * POST /api/superadmin/pricing/plans/:id/included-stores.
 *
 * Like a price edit this PUBLISHES A NEW PLAN VERSION (the allowance lives on
 * the version's "Per active store" charge), so it is allowed with live
 * subscribers: they keep their pinned allowance until repointed. 409 when
 * the plan has no per-active-store price to carry it.
 *
 * @param {number|string} planId  Plan id.
 * @param {number} includedCount  Integer >= 0.
 * @returns {Promise} Axios response; .data is { plan, version }.
 */
export function setPlanIncludedStores(planId, includedCount) {
    return axios.post(`${BASE}/plans/${planId}/included-stores`, {
        included_count: includedCount,
    });
}

/**
 * The SA "Subscription comp" section's data. Wraps
 * GET /api/superadmin/distributors/:id/comp. 404 = no such distributor or no
 * subscription (one uniform answer).
 *
 * @returns {Promise} .data is { comped, since, reason, charges[], history[] }.
 */
export function getDistributorComp(distributorId) {
    return axios.get(`/api/superadmin/distributors/${distributorId}/comp`);
}

/**
 * Comp a distributor (suppress every subscription charge, flat and
 * per-store). Wraps POST /api/superadmin/distributors/:id/comp. Idempotent.
 *
 * @param {number|string} distributorId
 * @param {string} reason  1-500 characters.
 * @returns {Promise} .data is the same shape as getDistributorComp.
 */
export function compDistributor(distributorId, reason) {
    return axios.post(`/api/superadmin/distributors/${distributorId}/comp`, { reason });
}

/**
 * End a distributor's comp. Wraps
 * POST /api/superadmin/distributors/:id/uncomp. Idempotent.
 */
export function uncompDistributor(distributorId, reason) {
    return axios.post(`/api/superadmin/distributors/${distributorId}/uncomp`, { reason });
}

/**
 * Set (or end, with amount_cents null) a custom rate on ONE subscription
 * charge of a distributor. Wraps
 * POST /api/superadmin/distributors/:id/custom-rate. 404: no such charge on
 * this distributor's plan; 409: the charge is comped.
 *
 * @param {number|string} distributorId
 * @param {{charge_id: number, amount_cents: (number|null), reason: string}} payload
 * @returns {Promise} .data is the getDistributorComp shape (the new state)
 *   plus { charge_id, amount_cents }.
 */
export function setCustomRate(distributorId, payload) {
    return axios.post(`/api/superadmin/distributors/${distributorId}/custom-rate`, payload);
}

/**
 * Pin (or, with null, clear) the distributor's included-store allowance. 0 is
 * a real pin (no free stores). Wraps
 * POST /api/superadmin/distributors/:id/included-stores-override.
 *
 * @param {number|string} distributorId
 * @param {(number|null)} includedStores  Integer >= 0, or null = follow the plan.
 * @param {string} reason  1-500 characters.
 * @returns {Promise} .data is the getDistributorComp shape (the new state)
 *   plus { included_stores }.
 */
export function setIncludedStoresOverride(distributorId, includedStores, reason) {
    return axios.post(`/api/superadmin/distributors/${distributorId}/included-stores-override`, {
        included_stores: includedStores,
        reason,
    });
}

/**
 * Reassign a distributor's subscription onto a DIFFERENT plan (the
 * SA-assignable Tier B mechanism). Wraps
 * POST /api/superadmin/pricing/distributors/:id/reassign-plan.
 *
 * Moves plan_id ACROSS plans and pins the target plan's default-for-new
 * version (plan_version_id + tier_key). Audit-logged server-side. Idempotent:
 * reassigning to the current plan returns status "already_on_plan".
 *
 * @param {number|string} distributorId  The distributor to reassign.
 * @param {object} target  Exactly one of { plan_id } or { plan_key }.
 * @returns {Promise} Axios response; .data carries { status, plan_id,
 *   plan_key, plan_version_id, tier_key }.
 */
export function reassignDistributorPlan(distributorId, target) {
    return axios.post(
        `${BASE}/distributors/${distributorId}/reassign-plan`,
        target,
    );
}

/**
 * Fetch the seeded decoration pricing grids for read-only SA display. Wraps
 * GET /api/superadmin/pricing/decoration-grids.
 *
 * Returns { tiers: [{ plan_id, plan_key, plan_name, is_public, grids: [...] }] }
 * where each grid is a self-describing tier_config blob (embroidery 2-D
 * qty x stitch, DTF 1-D qty), carrying co-located platform_price_cents +
 * decorator_payout_cents per cell. READ-ONLY -- there is no edit endpoint.
 */
export function getDecorationGrids() {
    return axios.get(`${BASE}/decoration-grids`);
}

/**
 * Create a new subscription plan (SA Plans & Pricing "New Plan").
 * Wraps POST /api/superadmin/pricing/plans.
 *
 * Body: { name (required), monthly_price_cents? (int|null),
 *         per_store_price_cents? (int|null -- per-live-store monthly charge,
 *         pinned into the new plan's v1 version so it grandfathers),
 *         app_fee_basis_points? (int), is_public? (bool, defaults false) }.
 * The new plan clones Tier A's decoration grids and is created hidden
 * (is_public=false) + active. Returns the created plan in list_plans shape.
 *
 * @param {object} payload  { name, monthly_price_cents?, ... }.
 * @returns {Promise} Axios response; .data is the created plan row.
 */
export function createPlan(payload) {
    return axios.post(`${BASE}/plans`, payload);
}

/**
 * Soft-delete (archive) a plan. Wraps
 * POST /api/superadmin/pricing/plans/:id/archive.
 *
 * Guarded server-side: 409 if ANY distributor_subscription references the
 * plan. Archived plans are hidden from every live read. Not a hard delete.
 *
 * @param {number|string} planId  Plan id.
 * @returns {Promise} Axios response; .data carries { id, key, status }.
 */
export function archivePlan(planId) {
    return axios.post(`${BASE}/plans/${planId}/archive`);
}

/**
 * Disable a plan (is_active=false), atomically moving subscribers. Wraps
 * POST /api/superadmin/pricing/plans/:id/disable.
 *
 * Body: { target_plan_id? } -- REQUIRED (a different, active, non-archived
 * plan) when the plan has subscribers; omitted when it has none. On the
 * subscribers path the backend reassigns ALL subscribers to the target's
 * default-for-new version THEN deactivates, in one transaction. 409 if
 * subscribers exist and no target given.
 *
 * @param {number|string} planId  Plan id to disable.
 * @param {object} [payload]  { target_plan_id } or {}.
 * @returns {Promise} Axios response; .data carries { id, key, status,
 *   reassigned_subscribers, target_plan_id }.
 */
export function disablePlan(planId, payload = {}) {
    return axios.post(`${BASE}/plans/${planId}/disable`, payload);
}


/**
 * Supplier availability per subscription tier.
 *
 * Jake, 2026-08-27: THE TIER CARRIES THE SUPPLIER LIST. Returns
 * `{ tiers: [{tier_key, tier_name, is_active, suppliers: [...]}],
 *    unconfigured_suppliers: [{supplier_key, name}] }`.
 *
 * `unconfigured_suppliers` is not decoration: under a tier-carries-the-list
 * model, a supplier no tier has a row for is invisible everywhere with
 * nothing to indicate a decision was made. The caller MUST surface it.
 */
export function getTierSuppliers() {
    return axios.get(`${BASE}/tier-suppliers`);
}

/**
 * Set one tier's supplier list.
 *
 * `suppliers` is an object of supplier_key -> boolean. Send EVERY supplier,
 * enabled or not -- a disabled row is what records that somebody decided,
 * and sending only the enabled ones re-opens the silent-omission gap.
 */
export function setTierSuppliers(tierKey, suppliers) {
    return axios.put(`${BASE}/tier-suppliers`, {
        tier_key: tierKey,
        suppliers,
    });
}

/**
 * White-label storefront branding, per subscription tier.
 *
 * Jake, 2026-09-18/19: white-labeling is "a feature of the subscription tier
 * system, and should default to off/disabled". Returns
 * `{ tiers: [{tier_key, tier_name, is_active, is_enabled}] }` with EVERY
 * tier present -- a tier with no row reads `is_enabled: false` rather than
 * being omitted, so its plan card always has a control to turn it on with.
 */
export function getTierWhiteLabel() {
    return axios.get(`${BASE}/tier-white-label`);
}

/**
 * Set ONE tier's white-label flag.
 *
 * PATCH, not PUT: the body names one tier and carries one boolean, so there
 * is no full collection to replace. Resolves to the server's fresh view of
 * every tier, so the caller need not guess what landed.
 */
export function setTierWhiteLabel(tierKey, isEnabled) {
    return axios.patch(`${BASE}/tier-white-label`, {
        tier_key: tierKey,
        is_enabled: isEnabled,
    });
}

/**
 * Per-distributor supplier overrides (the DDP control).
 *
 * Each entry carries `tier_enabled` (what the tier says), `override`
 * (null when none) and `effective` (what the resolver answers). The UI
 * needs all three so an SA can tell a deliberate override from a default
 * they are about to clobber.
 */
export function getDistributorSupplierOverrides(distributorId) {
    return axios.get(
        `/api/superadmin/distributors/${distributorId}/supplier-overrides`
    );
}

/**
 * Set per-distributor overrides.
 *
 * A value of `null` CLEARS the override so the supplier inherits the tier
 * again. Without that, every save ratchets one more distributor
 * permanently off the tier list.
 */
export function setDistributorSupplierOverrides(distributorId, suppliers) {
    return axios.put(
        `/api/superadmin/distributors/${distributorId}/supplier-overrides`,
        { suppliers }
    );
}

/**
 * Digitizing / vectorizing charges per tier (Trello #572).
 *
 * Returns `{ tiers: [{tier_key, tier_name, is_active, billing_mode,
 * digitizing_cents, vectorizing_cents, is_configured}], defaults }` with
 * EVERY tier present; a tier with no row reads the decided defaults
 * (charge immediately, 2500 / 1000) with `is_configured: false`.
 */
export function getTierArtServices() {
    return axios.get(`${BASE}/tier-art-services`);
}

/**
 * Replace ONE tier's art-service setting. PUT: the body is the tier's whole
 * setting (`{tier_key, billing_mode, digitizing_cents, vectorizing_cents}`,
 * all required). Resolves to the server's fresh view of every tier.
 */
export function setTierArtServices(setting) {
    return axios.put(`${BASE}/tier-art-services`, setting);
}
